// frontend/src/components/assessment/artifacts/AssessmentReport.js
//
// Artifact panel component for the "reporting" phase.
// Renders a loading/waiting state while the backend generates the report,
// then displays the full ConversationReport once data is available.
//
// Props:
//   report             — the report data object (null until complete)
//   currentSubphase     — 'creatingreport' | 'waiting' | 'active' | 'confirming'
//   reportFailure       — { reason } once polling detects a failure/timeout, else null
//   reportBodyError     — true if the report finished but its body fetch failed
//   onRetryReportBody   — retry the body fetch; called from the error state below

import { useState } from 'react';
import PropTypes from 'prop-types';
import ConversationReport from './AssessmentForm';

// ─── loading states ───────────────────────────────────────────────────────────

function PulsingDot() {
  return (
    <span style={{
      display: 'inline-block',
      width: 8, height: 8,
      borderRadius: '50%',
      background: 'var(--color-text-secondary)',
      animation: 'report-pulse 1.4s ease-in-out infinite',
      marginRight: 6,
    }} />
  );
}

function LoadingState({ title, detail }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem 2rem',
      textAlign: 'center',
      gap: '0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        <PulsingDot />
        <PulsingDot style={{ animationDelay: '0.2s' }} />
        <PulsingDot style={{ animationDelay: '0.4s' }} />
      </div>
      <p style={{
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--color-text-primary)',
        margin: 0,
      }}>
        {title}
      </p>
      {detail && (
        <p style={{
          fontSize: 13,
          color: 'var(--color-text-secondary)',
          margin: 0,
          lineHeight: 1.6,
          maxWidth: 320,
        }}>
          {detail}
        </p>
      )}
    </div>
  );
}

LoadingState.propTypes = {
  title: PropTypes.string,
  detail: PropTypes.string,
};

function FailedState() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem 2rem',
      textAlign: 'center',
      gap: '0.75rem',
    }}>
      <p style={{
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--color-text-primary)',
        margin: 0,
      }}>
        Debrief Unavailable
      </p>
      <p style={{
        fontSize: 13,
        color: 'var(--color-text-secondary)',
        margin: 0,
        lineHeight: 1.6,
        maxWidth: 320,
      }}>
        Your debrief couldn&apos;t be generated. Please contact support if this persists.
      </p>
    </div>
  );
}

function BodyErrorState({ onRetry }) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem 2rem',
      textAlign: 'center',
      gap: '0.75rem',
    }}>
      <p style={{
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--color-text-primary)',
        margin: 0,
      }}>
        Your report is ready, but we couldn&apos;t load it
      </p>
      <p style={{
        fontSize: 13,
        color: 'var(--color-text-secondary)',
        margin: 0,
        lineHeight: 1.6,
        maxWidth: 320,
      }}>
        The assessment finished successfully — only the retrieval failed. Try again below.
      </p>
      <button
        type="button"
        onClick={handleRetry}
        disabled={retrying}
        className="btn btn-secondary btn-sm"
        style={{ marginTop: 4 }}
      >
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
}

BodyErrorState.propTypes = {
  onRetry: PropTypes.func,
};

// ─── main component ───────────────────────────────────────────────────────────

export default function AssessmentReport({ report, currentSubphase, reportFailure, reportBodyError, onRetryReportBody }) {
  if (reportFailure) {
    return <FailedState />;
  }

  if (reportBodyError && !report) {
    return <BodyErrorState onRetry={onRetryReportBody} />;
  }

  if (currentSubphase === 'creatingreport') {
    return (
      <LoadingState
        title="Generating your debrief…"
        detail="Analysing your preparation and conversation. This will take a minute or two."
      />
    );
  }

  if (currentSubphase === 'waiting') {
    return (
      <LoadingState
        title="Your debrief is being prepared…"
        detail="The analysis runs in three passes and typically takes 1–2 minutes. This panel will update automatically when it's ready."
      />
    );
  }

  if (!report) {
    return (
      <LoadingState
        title="Loading debrief…"
        detail={null}
      />
    );
  }

  return (
    <div style={{ padding: '0.5rem 1.25rem 1.5rem' }}>
      <ConversationReport data={report} />
    </div>
  );
}

AssessmentReport.propTypes = {
  report: PropTypes.object,
  currentSubphase: PropTypes.string,
  reportFailure: PropTypes.shape({ reason: PropTypes.string }),
  reportBodyError: PropTypes.bool,
  onRetryReportBody: PropTypes.func,
};
